<template>
  <Drawer v-model:open="isOpen" :direction="direction">
    <DrawerTrigger as-child>
      <button
        class="flex cursor-pointer items-center gap-1 px-2 py-1 font-['Spectral'] text-sm font-medium leading-tight tracking-normal transition-all duration-500 hover:!text-gold1"
        :class="shouldTextWhite ? 'text-white' : 'text-[#333]'"
      >
        {{ selectedCurrency }}
        <div
          class="h-2 w-2 bg-current"
          :style="{
            maskImage: `url(${iconArrow})`,
            WebkitMaskImage: `url(${iconArrow})`,
            maskSize: 'contain',
            WebkitMaskSize: 'contain',
            maskRepeat: 'no-repeat',
            WebkitMaskRepeat: 'no-repeat',
            maskPosition: 'center',
            WebkitMaskPosition: 'center',
          }"
        />
      </button>
    </DrawerTrigger>
    <DrawerContent class="bg-white" :class="direction === 'right' ? '!w-80' : ''" :position="direction">
      <!-- sticky ヘッダー -->
      <DrawerHeader class="sticky top-0 z-10 flex items-center gap-1 border-b border-light-bd-muted bg-white px-4 py-3">
        <DrawerTitle class="flex-1 font-['Spectral'] text-base font-semibold leading-6 text-light-fg-emphasis"> Change display currency </DrawerTitle>
        <button aria-label="Close" class="shrink-0 p-2 text-light-fg-muted transition-colors hover:text-light-fg-default" @click="() => (isOpen = false)">
          <svg class="h-6 w-6" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
      </DrawerHeader>

      <!-- 通貨リスト -->
      <div class="flex flex-col gap-2 p-4">
        <button
          v-for="currency in supportedCurrencies"
          :key="currency.code"
          class="flex cursor-pointer items-center gap-2 py-2 transition-colors"
          type="button"
          :class="selectedCurrency === currency.code ? 'text-light-fg-accent' : 'text-light-fg-muted'"
          @click="() => selectCurrency(currency.code)"
        >
          <div
            class="mr-2 h-6 w-6 shrink-0 bg-current"
            role="img"
            :aria-label="currency.code"
            :class="selectedCurrency === currency.code ? 'text-light-fg-accent' : 'text-light-fg-default'"
            :style="{
              maskImage: `url(${currency.icon})`,
              WebkitMaskImage: `url(${currency.icon})`,
              maskSize: 'contain',
              WebkitMaskSize: 'contain',
              maskRepeat: 'no-repeat',
              WebkitMaskRepeat: 'no-repeat',
              maskPosition: 'center',
              WebkitMaskPosition: 'center',
            }"
          />
          <span class="flex-1 text-left font-['Spectral'] text-base leading-6">{{ currency.code }} ({{ currency.label }})</span>
          <img v-if="selectedCurrency === currency.code" alt="check" class="h-4 w-4" :src="iconCheck" />
        </button>
      </div>

      <!-- 注記 -->
      <p class="px-4 pb-8 font-['Spectral'] text-base leading-6 text-light-fg-disabled">
        Prices use today's exchange rate. Final payment is processed in Japanese Yen, so the actual amount may vary slightly.
      </p>
    </DrawerContent>
  </Drawer>
</template>

<script setup lang="ts">
import { DrawerTrigger } from 'vaul-vue';

import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/lib/shadcn-vue/drawer';
import { useCurrency } from '@/wabunka-src/common/composables/general/useCurrency/useCurrency';
import { sendGAWabunkaChangeCurrencyEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/changeCurrency';

// public/ 配下のアイコン参照は :src バインディング経由で行う（コンパイル時 import 化を回避、JCAT-4981）
const iconArrow = '/icon/currencies/arrow.svg';
const iconCheck = '/icon/currencies/check.svg';

interface Props {
  shouldTextWhite?: boolean;
  direction?: 'bottom' | 'right';
  gaLocation: string;
}

const props = withDefaults(defineProps<Props>(), {
  shouldTextWhite: false,
  direction: 'bottom',
});

const { selectedCurrency, supportedCurrencies } = useCurrency();
const isOpen = ref(false);

const selectCurrency = (code: string) => {
  if (selectedCurrency.value === code) {
    isOpen.value = false;
    return;
  }
  selectedCurrency.value = code;
  sendGAWabunkaChangeCurrencyEvent(code, props.gaLocation);
  isOpen.value = false;
};
</script>
